import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/** Programmes de formation (écran `ped-ingenierie`, TF-472). */
export type StatutProgramme = 'BROUILLON' | 'VALIDE';
export type EtatBloc = 'INCHANGE' | 'AJOUTE' | 'MODIFIE';

export interface BlocAffiche {
  id: string | null;
  libelle: string;
  heures: number;
  /** Dans une révision, ce que devient le bloc ; `null` ailleurs. */
  etat: EtatBloc | null;
  /** Ce qui empêche de retirer ce bloc du programme, ou `null`. */
  refusRetrait: string | null;
}

export interface VersionProgramme {
  titre: string;
  niveau: string;
  niveauBpf: number | null;
  heures: number;
  heuresEcrits: number;
  heuresEcf: number;
  codeDiplome: string;
  rncp: string;
  blocs: BlocAffiche[];
  /** Blocs + écrits + ECF, à comparer au volume annoncé. */
  heuresAuProgramme: number;
}

export interface Changement {
  champ: string;
  avant: string;
  apres: string;
}

export interface VersionEnEdition extends VersionProgramme {
  revision: boolean;
  /** À renvoyer avec la validation. */
  version: string;
  ouverte: { le: string; par: string | null } | null;
  blocsRetires: { libelle: string; heures: number }[];
  changements: Changement[];
}

export interface Programme {
  id: string;
  code: string;
  statut: StatutProgramme;
  actif: boolean;
  socle: boolean;
  validation: { le: string; par: string | null } | null;
  /** Version en usage ; `null` pour un brouillon. */
  valide: VersionProgramme | null;
  /** Le brouillon, ou la révision ouverte ; `null` sinon. */
  enEdition: VersionEnEdition | null;
  refusValidation: string | null;
  refusSuppression: string | null;
  rattachements: { dossiers: number; sessions: number };
}

export interface EtatProgrammes {
  programmes: Programme[];
  niveaux: string[];
}

export interface BlocSaisi {
  id?: string;
  libelle: string;
  heures: number;
}

export interface ContenuSaisi {
  titre: string;
  niveau: string;
  heures: number;
  heuresEcrits: number;
  heuresEcf: number;
  codeDiplome: string;
  rncp: string;
  blocs: BlocSaisi[];
}

const CLE = ['programmes'];

export function useProgrammes() {
  return useQuery({ queryKey: CLE, queryFn: () => appelApi<EtatProgrammes>('/programmes') });
}

/**
 * Chaque geste relit la liste. Une validation change ce que les autres écrans
 * proposent : formations, référentiels, catalogue et sessions sont périmés.
 */
function useGeste<TEntree, TSortie>(requete: (entree: TEntree) => Promise<TSortie>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: requete,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE });
      void client.invalidateQueries({ queryKey: ['formations'] });
      void client.invalidateQueries({ queryKey: ['referentiels'] });
      void client.invalidateQueries({ queryKey: ['catalogue'] });
      void client.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

export function useCreerProgramme() {
  return useGeste((entree: ContenuSaisi & { code: string }) =>
    appelApi<Programme>('/programmes', { methode: 'POST', corps: entree }),
  );
}

/** Le brouillon (code compris), ou la révision d'un programme validé. */
export function useModifierProgramme() {
  return useGeste(({ id, ...entree }: ContenuSaisi & { id: string; code?: string }) =>
    appelApi<Programme>(`/programmes/${encodeURIComponent(id)}`, { methode: 'PUT', corps: entree }),
  );
}

export function useValiderProgramme() {
  return useGeste(({ id, version }: { id: string; version: string }) =>
    appelApi<Programme>(`/programmes/${encodeURIComponent(id)}/validation`, {
      methode: 'POST',
      corps: { version },
    }),
  );
}

export function useAbandonnerRevision() {
  return useGeste((id: string) =>
    appelApi<Programme>(`/programmes/${encodeURIComponent(id)}/revision`, { methode: 'DELETE' }),
  );
}

export function useSupprimerProgramme() {
  return useGeste((id: string) =>
    appelApi<void>(`/programmes/${encodeURIComponent(id)}`, { methode: 'DELETE' }),
  );
}
